import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * Cloudflare Turnstile widget — thin wrapper around the official CF script.
 *
 * Loads `https://challenges.cloudflare.com/turnstile/v0/api.js` once per
 * page, renders an invisible/non-interactive challenge inline, and reports
 * the solved token back via `onToken`. On expire/error the parent is told
 * to clear the token so the form can't submit a stale value.
 *
 * Two modes:
 *   - default (`mode="passive"`): widget renders eagerly on mount and pushes
 *     a token via `onToken` once CF resolves the challenge.
 *   - `mode="execute"`: widget stays idle until the parent calls
 *     `ref.current.execute()`. The returned promise resolves with the token
 *     (or `'cf-script-load-failed'` on script-load failure, matching the
 *     fail-open contract). Use this when you want the verification delay
 *     folded into the submit click instead of blocking the user up-front.
 *
 * If `siteKey` is empty (env var unset), the widget renders nothing — the
 * BE middleware is also a no-op in that case, so this fails open during
 * staged rollout.
 *
 * F-RATE-06 follow-up. See SECURITY_AUDIT/02_findings.md.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          'timeout-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          appearance?: 'always' | 'execute' | 'interaction-only';
          execution?: 'render' | 'execute';
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
      execute: (widgetIdOrContainer: string | HTMLElement) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

export interface TurnstileWidgetHandle {
  /**
   * Trigger the challenge and resolve with a fresh single-use token. In
   * execute mode this calls `turnstile.execute()` and waits; in passive
   * mode it waits for the next CF auto-callback.
   *
   * Each call yields a new token — the widget is reset after every
   * successful resolve to avoid replaying single-use tokens.
   *
   * Resolves with `'cf-script-load-failed'` if the CF script failed to
   * load — same fail-open sentinel the passive `onToken` path uses.
   */
  execute: () => Promise<string>;
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Hard ceiling for an execute()-mode challenge before it fails open. If
// Cloudflare's challenge platform neither returns a token nor fires an
// error/timeout callback within this window — e.g. an ad-blocker or DNS filter
// blocks a challenge sub-host such as brunhild.challenges.cloudflare.com —
// execute() resolves with the fail-open sentinel so the caller proceeds
// instead of hanging. The BE accepts the sentinel and its own limiters apply.
const EXECUTE_TIMEOUT_MS = 12_000;

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      // Another mount started loading the script first.
      const onLoad = () => resolve();
      const onError = () => reject(new Error('Turnstile script failed to load'));
      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', onError, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Turnstile script failed to load')), {
      once: true,
    });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

interface TurnstileWidgetProps {
  siteKey: string;
  onToken?: (token: string | null) => void;
  mode?: 'passive' | 'execute';
}

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ siteKey, onToken, mode = 'passive' }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const scriptFailedRef = useRef(false);
    const readyRef = useRef(false);
    const pendingResolversRef = useRef<Array<(token: string) => void>>([]);

    const resolvePending = (token: string) => {
      const resolvers = pendingResolversRef.current;
      pendingResolversRef.current = [];
      resolvers.forEach((r) => r(token));
    };

    useEffect(() => {
      if (!siteKey) return;
      let cancelled = false;

      loadTurnstileScript()
        .then(() => {
          if (cancelled || !containerRef.current || !window.turnstile) return;
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => {
              // Hand off the token, then reset the widget so the next
              // execute() call gets a fresh one — Turnstile tokens are
              // single-use; replaying triggers `timeout-or-duplicate`.
              onToken?.(token);
              resolvePending(token);
              if (mode === 'execute' && widgetIdRef.current && window.turnstile) {
                try {
                  window.turnstile.reset(widgetIdRef.current);
                } catch {
                  // ignore
                }
              }
            },
            // A challenge that errors, times out, or expires before producing a
            // token must not strand an awaiting execute() caller. Passive mode
            // keeps its existing "clear the token" contract; execute mode
            // additionally fails open (resolvePending with the sentinel) so the
            // sandbox /try flow can't hang when a challenge sub-host is blocked.
            'error-callback': () => {
              onToken?.(null);
              if (mode === 'execute') resolvePending('cf-script-load-failed');
            },
            'expired-callback': () => {
              onToken?.(null);
              if (mode === 'execute') resolvePending('cf-script-load-failed');
            },
            'timeout-callback': () => {
              onToken?.(null);
              if (mode === 'execute') resolvePending('cf-script-load-failed');
            },
            appearance: mode === 'execute' ? 'execute' : 'interaction-only',
            ...(mode === 'execute' ? { execution: 'execute' as const } : {}),
          });
          readyRef.current = true;
          // execute() is commonly called before this async script load resolves
          // (the /try page fires it on mount, before the CF script is ready), so
          // the challenge would never be triggered. If a caller is already
          // waiting, kick it now.
          if (mode === 'execute' && pendingResolversRef.current.length > 0 && widgetIdRef.current) {
            try {
              window.turnstile.execute(widgetIdRef.current);
            } catch {
              // ignore — a token/error callback or execute()'s timeout settles it
            }
          }
        })
        .catch(() => {
          // Script failed to load (network / CSP). Tell the parent to allow
          // the submit anyway; the BE limiters are still in place. Better to
          // accept the marginal abuse risk than block legitimate signups
          // because Cloudflare is having a bad day.
          scriptFailedRef.current = true;
          onToken?.('cf-script-load-failed');
          resolvePending('cf-script-load-failed');
        });

      return () => {
        cancelled = true;
        readyRef.current = false;
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // ignore — widget may already be gone
          }
          widgetIdRef.current = null;
        }
      };
    }, [siteKey, onToken, mode]);

    useImperativeHandle(
      ref,
      () => ({
        execute: () =>
          new Promise<string>((resolve) => {
            if (scriptFailedRef.current) {
              resolve('cf-script-load-failed');
              return;
            }
            // Settle exactly once — whichever of a token callback, an
            // error/timeout callback, or the hard timeout below fires first.
            // The timeout guarantees execute() can never hang, even if the
            // challenge platform stalls silently (fires no callback at all).
            let settled = false;
            let timer = 0;
            const settle = (token: string) => {
              if (settled) return;
              settled = true;
              if (timer) window.clearTimeout(timer);
              resolve(token);
            };
            timer = window.setTimeout(() => settle('cf-script-load-failed'), EXECUTE_TIMEOUT_MS);
            pendingResolversRef.current.push(settle);
            if (mode === 'execute' && readyRef.current && widgetIdRef.current && window.turnstile) {
              try {
                window.turnstile.execute(widgetIdRef.current);
              } catch {
                // Not ready yet — the post-render kick or the timeout settles it.
              }
            }
            // Passive mode: wait for CF's auto-callback (or the timeout) to settle.
          }),
      }),
      [mode],
    );

    if (!siteKey) return null;
    return <div ref={containerRef} />;
  },
);

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
